import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSession, saveSession, type Company } from '../lib/session.ts';
import { getJson } from '../lib/api.ts';

const CompaniesPage = () => {
  const navigate = useNavigate();
  const [session, setSession] = useState(getSession());
  const [companies, setCompanies] = useState<Array<Company>>([]);
  const [error, setError] = useState('');
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!session || !session.otp) {
      navigate('/identification');
    }
  }, [navigate, session]);

  // Os dois useEffect vem antes de qualquer `return`: hook so' pode ser chamado na mesma
  // ordem em todo render. O `if (!session) return null` estava entre eles, entao no render
  // em que a sessao sumia o React via um hook a menos — e a partir dali o estado de um
  // hook passa a responder pelo outro. O guard foi para depois dos dois; quem depende da
  // sessao ja' se protege sozinho.
  useEffect(() => {
    if (!session?.customerId) return;

    const fetchCompanies = async () => {
      setLoaded(false);
      try {
        const data = await getJson<Array<Company>>(
          `/api/companies/${session?.customerId}`
        );
        setCompanies(data);
      } catch (error) {
        console.error(error);
        setError((error as Error).message || 'Erro ao buscar empresas.');
      } finally {
        setLoaded(true);
      }
    };
    fetchCompanies();
  }, [session]);

  if (!session) return null;

  const handleCompanySelect = async (company: Company) => {
    saveSession({
      ...session,
      selectedCompany: company,
    });
    navigate(`/charges/${company.id}`);
  };

  return (
    <main className="grid gap-5 grid-cols-1 md:grid-cols-2">
      <section className="md:col-span-2 rounded-3xl bg-white p-8 shadow-md shadow-slate-200">
        <p className="text-sm uppercase tracking-[0.3em] text-red-600">Sua negociação</p>
        <h1 className="mt-4 text-3xl font-semibold text-red-600">Escolha a empresa para renegociar</h1>
        <p className="mt-3 text-slate-500">O melhor caminho é pagar à vista, mas você pode escolher renegociar se precisar.</p>
      </section>

      {error && <div className="md:col-span-2 rounded-2xl bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      {companies.map((company) => (
        <article key={company.id} className="md:col-span-2 rounded-3xl border border-slate-200 bg-white p-8 shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
          <div className="flex items-start gap-4">
            <div className="h-14 w-14 rounded-2xl">
              <img src="/favicon.ico" />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-600">{company.name}</p>
              <p className="mt-1 text-sm text-slate-500">{company.legal_name}</p>
              <p className="mt-1 text-sm text-slate-500">{company.address}</p>
              <p className="mt-1 text-sm text-slate-500">{company.phone}</p>
            </div>
          </div>
          <div className="mt-6 space-y-3">
            <div className="rounded-3xl bg-red-50 p-4">
              <p className="text-xs uppercase tracking-[0.35em] text-slate-500">Desconto à vista</p>
              <p className="mt-2 text-3xl font-semibold text-slate-600">{company.configs.payment.full_settlement_discount_rate}%</p>
              <p className="mt-1 text-sm text-slate-500">do valor total ao pagar à vista</p>
            </div>
            <div className="rounded-3xl bg-red-50 p-4">
              <p className="text-xs uppercase tracking-[0.35em] text-slate-500">Entrada mínima</p>
              <p className="mt-2 text-lg font-semibold text-slate-600">{company.configs.payment.minimum_down_payment_rate}%</p>
              <p className="mt-1 text-sm text-slate-500">do saldo para começar a renegociação</p>
            </div>
          </div>
          <button
            onClick={() => handleCompanySelect(company)}
            className="mt-6 inline-flex w-full justify-center rounded-2xl bg-red-600 text-white px-5 py-4 text-sm font-semibold transition hover:bg-red-700"
          >
            Ver débitos e negociar
          </button>
        </article>
      ))}

      {loaded && !error && companies.length === 0 && (
        <div className="md:col-span-2 rounded-2xl bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Houve um problema para recuperar as unidades que há. Entre em contato com a unidade.
        </div>
      )}
    </main>
  );
};

export default CompaniesPage;
